import { useState, useCallback } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, CopyButton, Tabs, TabsList, TabsTrigger, TabsContent } from '@devkit/ui';
import { Code, RefreshCw } from 'lucide-react';
import { encodeHtmlEntities, decodeHtmlEntities } from '@devkit/tools-encoding';
import { trackToolExecute, trackCopy, trackClear } from '@devkit/analytics';

const defaultHtml = `<div class="card">
  <h1>Hello & welcome!</h1>
  <p>DevKit's HTML Entity Encoder helps you handle special characters like <, >, &, " and '.</p>
  <script>alert("XSS attempt")</script>
</div>`;

export default function HtmlEntityEncoderPage() {
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleEncode = useCallback(() => {
    setError(null);
    const result = encodeHtmlEntities(input);
    if (!result.success || !result.data) {
      setError(result.error ?? 'Failed to encode.');
      setOutput('');
      return;
    }
    setOutput(result.data);
    trackToolExecute('html-entity-encoder', 'encode');
  }, [input]);

  const handleDecode = useCallback(() => {
    setError(null);
    const result = decodeHtmlEntities(input);
    if (!result.success || !result.data) {
      setError(result.error ?? 'Failed to decode.');
      setOutput('');
      return;
    }
    setOutput(result.data);
    trackToolExecute('html-entity-encoder', 'decode');
  }, [input]);

  const handleSwap = useCallback(() => {
    setInput(output);
    setOutput('');
    setMode(mode === 'encode' ? 'decode' : 'encode');
  }, [output, mode]);

  const clearAll = useCallback(() => {
    setInput('');
    setOutput('');
    setError(null);
    trackClear('html-entity-encoder');
  }, []);

  const handleProcess = () => {
    if (mode === 'encode') handleEncode();
    else handleDecode();
  };

  const handleLoadSample = () => {
    setInput(defaultHtml);
    if (mode === 'decode') setMode('encode');
  };

  return (
    <ToolLayout slug="html-entity-encoder">
      <Card>
        <CardContent className="p-6">
          <Tabs
            value={mode}
            onValueChange={(v) => {
              setMode(v as 'encode' | 'decode');
              setError(null);
            }}
          >
            <TabsList>
              <TabsTrigger value="encode">Encode</TabsTrigger>
              <TabsTrigger value="decode">Decode</TabsTrigger>
            </TabsList>

            <TabsContent value="encode">
              <p className="text-sm text-slate-400 mb-4">
                Convert special HTML characters to entities to prevent XSS and ensure safe rendering.
              </p>
            </TabsContent>

            <TabsContent value="decode">
              <p className="text-sm text-slate-400 mb-4">
                Convert HTML entities back to their original characters.
              </p>
            </TabsContent>
          </Tabs>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-300">
                {mode === 'encode' ? 'HTML Input' : 'Encoded Input'}
              </label>
              <Button variant="ghost" size="sm" onClick={handleLoadSample}>
                Load Sample
              </Button>
            </div>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                mode === 'encode'
                  ? 'Paste HTML or text with special characters...'
                  : 'Paste HTML entities like &lt; &amp; &#39; ...'
              }
              className="w-full h-40 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
            />
          </div>

          <div className="flex gap-2 mb-4">
            <Button onClick={handleProcess} className="gap-2">
              <Code className="w-4 h-4" />
              {mode === 'encode' ? 'Encode' : 'Decode'}
            </Button>
            <Button variant="outline" onClick={handleSwap} className="gap-2" disabled={!output}>
              <RefreshCw className="w-4 h-4" />
              Swap
            </Button>
            <Button variant="ghost" onClick={clearAll}>
              Clear
            </Button>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-300">
                {mode === 'encode' ? 'Encoded Output' : 'Decoded Output'}
              </label>
              {output && <CopyButton text={output} onCopy={() => trackCopy('html-entity-encoder')} />}
            </div>
            <textarea
              value={error ? '' : output}
              readOnly
              placeholder={error ? '' : 'Result will appear here...'}
              className={`w-full h-40 px-4 py-3 bg-surface border rounded-lg font-mono text-sm text-slate-100 resize-y ${
                error ? 'border-red-500' : 'border-slate-700'
              }`}
            />
            {error && (
              <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </ToolLayout>
  );
}
